import { Component, h, clamp, register, registerFieldType } from './core.js';
import { renderIcon, iconHtml } from './icons.js';

/**
 * Rating: a row of icons the user clicks or steps through with the keyboard.
 *
 * new dfc.Rating(container, {
 *   value: 0, max: 5, allowHalf: false, allowClear: true, readOnly: false,
 *   disabled: false, size: 'md' | 'sm' | 'lg',
 *   icons: { filled: 'star', empty: 'star-outline' }   // any icon spec: built-in name, SVG string, URL, { className }
 *   labels: null | ['Poor', 'Fair', ...], showValue: false, name: null
 * })
 * Events: valueChanged { value, previous, source }, hover { value }
 * Static: dfc.Rating.renderStatic(value, { max, icon }) -> HTML string
 */
export class Rating extends Component {
	static get componentName() { return 'rating'; }

	defaults() {
		return { value: 0, max: 5, allowHalf: false, allowClear: true, readOnly: false, disabled: false, size: 'md', icon: null, icons: null, labels: null, showValue: false, name: null };
	}

	render() {
		const o = this.options;
		this._value = this._normalise(o.value);
		this.el.className = `dfc dfc-rating dfc-size-${o.size}` + (o.readOnly ? ' is-readonly' : '') + (o.disabled ? ' is-disabled' : '') + (o.className ? ' ' + o.className : '');
		this.el.innerHTML = '';
		const interactive = !o.readOnly && !o.disabled;
		this.track = h('div', {
			className: 'dfc-rating-track',
			role: 'slider',
			tabindex: interactive ? '0' : '-1',
			'aria-valuemin': '0',
			'aria-valuemax': String(o.max),
			'aria-valuenow': String(this._value),
			'aria-valuetext': this._label(this._value),
			'aria-readonly': o.readOnly ? 'true' : null,
			'aria-disabled': o.disabled ? 'true' : null,
			'aria-label': o.ariaLabel || 'Rating',
		});
		this.items = [];
		for (let i = 1; i <= o.max; i++) {
			const icons = Rating._icons(o);
			const item = h('span', { className: 'dfc-rating-item', dataset: { index: String(i) }, title: o.labels && o.labels[i - 1] ? o.labels[i - 1] : null },
				renderIcon(icons.empty, 'dfc-rating-icon dfc-rating-empty'),
				h('span', { className: 'dfc-rating-fill' }, renderIcon(icons.filled, 'dfc-rating-icon')));
			this.items.push(item);
			this.track.appendChild(item);
		}
		this.el.appendChild(this.track);
		if (o.name) {
			this.input = h('input', { type: 'hidden', name: o.name, value: String(this._value) });
			this.el.appendChild(this.input);
		}
		if (o.showValue) {
			this.valueEl = h('span', { className: 'dfc-rating-value', 'aria-hidden': 'true' });
			this.el.appendChild(this.valueEl);
		}
		this._paint(this._value);
	}

	bind() {
		this._onMove = (e) => {
			if (!this._interactive()) return;
			const v = this._valueFromEvent(e);
			if (v === null) return;
			this._paint(v);
			this.emit('hover', { value: v });
		};
		this._onLeave = () => { if (this._interactive()) this._paint(this._value); };
		this._onClick = (e) => {
			if (!this._interactive()) return;
			const v = this._valueFromEvent(e);
			if (v === null) return;
			this._set(this.options.allowClear && v === this._value ? 0 : v, 'user');
		};
		this._onKey = (e) => {
			if (!this._interactive()) return;
			const step = this.options.allowHalf ? 0.5 : 1;
			let v = this._value;
			if (e.key === 'ArrowRight' || e.key === 'ArrowUp') v += step;
			else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') v -= step;
			else if (e.key === 'Home') v = 0;
			else if (e.key === 'End') v = this.options.max;
			else if (e.key === 'Backspace' || e.key === 'Delete') v = 0;
			else return;
			e.preventDefault();
			this._set(v, 'user');
		};
		this.el.addEventListener('mousemove', this._onMove);
		this.el.addEventListener('mouseleave', this._onLeave);
		this.el.addEventListener('click', this._onClick);
		this.el.addEventListener('keydown', this._onKey);
	}

	unbind() {
		if (!this.el) return;
		this.el.removeEventListener('mousemove', this._onMove);
		this.el.removeEventListener('mouseleave', this._onLeave);
		this.el.removeEventListener('click', this._onClick);
		this.el.removeEventListener('keydown', this._onKey);
	}

	get value() { return this._value; }
	set value(v) { this._set(v, 'api'); }

	_interactive() { return !this.options.readOnly && !this.options.disabled && !this._destroyed; }

	_normalise(v) {
		let n = Number(v) || 0;
		n = this.options.allowHalf ? Math.round(n * 2) / 2 : Math.round(n);
		return clamp(n, 0, this.options.max);
	}

	_label(v) {
		const o = this.options;
		const whole = Math.round(v);
		if (o.labels && o.labels[whole - 1] && v === whole) return o.labels[whole - 1];
		return `${v} of ${o.max}`;
	}

	_valueFromEvent(e) {
		const item = e.target.closest && e.target.closest('.dfc-rating-item');
		if (!item || !this.el.contains(item)) return null;
		const index = Number(item.dataset.index);
		if (!this.options.allowHalf) return index;
		const rect = item.getBoundingClientRect();
		const half = (e.clientX - rect.left) < rect.width / 2;
		return half ? index - 0.5 : index;
	}

	_paint(v) {
		this.items.forEach((item, i) => {
			const idx = i + 1;
			const fill = v >= idx ? 100 : v >= idx - 0.5 ? 50 : 0;
			item.querySelector('.dfc-rating-fill').style.width = fill + '%';
			item.classList.toggle('is-filled', fill > 0);
		});
		if (this.valueEl) this.valueEl.textContent = this._label(v);
	}

	_set(v, source) {
		const next = this._normalise(v);
		const previous = this._value;
		this._value = next;
		this._paint(next);
		this.track.setAttribute('aria-valuenow', String(next));
		this.track.setAttribute('aria-valuetext', this._label(next));
		if (this.input) this.input.value = String(next);
		this._valueChanged(next, previous, source);
	}

	/** Resolves the icon pair: `icons` wins, then `icon: 'name'` maps to name / name-outline, else stars. */
	static _icons(o) {
		if (o.icons && typeof o.icons === 'object' && (o.icons.filled || o.icons.empty)) {
			const filled = o.icons.filled || 'star';
			const empty = o.icons.empty || (typeof filled === 'string' && /^[a-z-]+$/.test(filled) ? filled + '-outline' : 'star-outline');
			return { filled, empty };
		}
		if (typeof o.icon === 'string' && o.icon) return { filled: o.icon, empty: /^[a-z-]+$/.test(o.icon) ? o.icon + '-outline' : 'star-outline' };
		return { filled: 'star', empty: 'star-outline' };
	}

	/** Read-only HTML for grid cells and summaries. */
	static renderStatic(value, opts = {}) {
		const max = opts.max || 5, icons = Rating._icons(opts);
		const v = clamp(Number(value) || 0, 0, max);
		let html = `<span class="dfc dfc-rating dfc-size-${opts.size || 'sm'} is-readonly is-static" role="img" aria-label="${v} of ${max}"><span class="dfc-rating-track">`;
		for (let i = 1; i <= max; i++) {
			const fill = v >= i ? 100 : v >= i - 0.5 ? 50 : 0;
			html += `<span class="dfc-rating-item${fill ? ' is-filled' : ''}">${iconHtml(icons.empty, 'dfc-rating-icon dfc-rating-empty')}<span class="dfc-rating-fill" style="width:${fill}%">${iconHtml(icons.filled, 'dfc-rating-icon')}</span></span>`;
		}
		return html + '</span></span>';
	}
}

register('rating', Rating);
registerFieldType('rating', {
	create: (container, field, value) => new Rating(container, { value: value ?? 0, max: field.max || 5, allowHalf: !!field.allowHalf, readOnly: !!field.readonly, labels: field.labels || null, name: field.id }),
	getValue: (inst) => inst.value,
	setValue: (inst, v) => { inst.value = v; },
});
